import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, Calendar, TrendingUp, TrendingDown } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface AttendanceSummary {
  classId: number;
  className: string;
  totalStudents: number;
  presentCount: number;
  absentCount: number;
  lateCount: number;
  excusedCount: number;
}

interface ClassOption {
  id: number;
  name: string;
}

const HeadteacherAttendance: React.FC = () => {
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [month, setMonth] = useState<string>(String(new Date().getMonth() + 1));
  const [year, setYear] = useState<string>(String(new Date().getFullYear()));
  const [summary, setSummary] = useState<AttendanceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  useAutoDismiss(message, setMessage, null, 5000);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(res.data?.classes || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  }, [token]);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (selectedClass) params.classId = selectedClass;
      if (month) params.month = month;
      if (year) params.year = year;

      const res = await axios.get(getApiUrl("headteacher/attendance"), {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setSummary(res.data?.summary || []);
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error fetching attendance data");
    } finally {
      setLoading(false);
    }
  }, [token, selectedClass, month, year]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const getAttendanceRate = (present: number, total: number) => {
    if (total === 0) return "0.0";
    return ((present / total) * 100).toFixed(1);
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>Attendance Overview</h2>
              <p className="page-subtitle text-slate-600 font-medium">Monitor student attendance across all classes.</p>
            </div>
          </div>

          {message && (
            <div className="p-4 mb-4 rounded-xl bg-red-50 text-red-700 text-sm">
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <select
              className="utopia-input"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls.id} value={String(cls.id)}>{cls.name}</option>
              ))}
            </select>
            <select
              className="utopia-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </option>
              ))}
            </select>
            <input
              className="utopia-input"
              type="number"
              min="2000"
              max="2100"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
            <button className="utopia-btn-primary" onClick={fetchAttendance}>
              View Report
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading attendance data...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-700 font-bold">Total Classes</p>
                  <p className="text-2xl font-bold text-slate-900">{summary.length}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-700 font-bold">Total Present</p>
                  <p className="text-2xl font-bold text-teal-600">
                    {summary.reduce((acc, s) => acc + (Number(s.presentCount) || 0), 0)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-700 font-bold">Total Absent</p>
                  <p className="text-2xl font-bold text-red-600">
                    {summary.reduce((acc, s) => acc + (Number(s.absentCount) || 0), 0)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-700 font-bold">Total Late</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {summary.reduce((acc, s) => acc + (Number(s.lateCount) || 0), 0)}
                  </p>
                </div>
              </div>

              <table className="utopia-table">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Students</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Late</th>
                    <th>Attendance Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((item) => {
                    const total = (Number(item.presentCount) || 0) + (Number(item.absentCount) || 0) + (Number(item.lateCount) || 0);
                    const rate = getAttendanceRate(Number(item.presentCount) || 0, total);
                    return (
                      <tr key={item.classId}>
                        <td className="font-medium">{item.className}</td>
                        <td>{item.totalStudents}</td>
                        <td className="text-teal-600">{String(item.presentCount)}</td>
                        <td className="text-red-600">{String(item.absentCount)}</td>
                        <td className="text-amber-600">{String(item.lateCount)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${parseFloat(rate) >= 90 ? "text-teal-600" : parseFloat(rate) >= 75 ? "text-amber-600" : "text-red-600"}`}>
                              {rate}%
                            </span>
                            {parseFloat(rate) >= 90 ? (
                              <TrendingUp size={16} className="text-teal-600" />
                            ) : (
                              <TrendingDown size={16} className="text-red-600" />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {summary.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-slate-500 py-8">
                        No attendance data available for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HeadteacherAttendance;











